import React, { memo } from "react";
import {
  Autocomplete,
  Button,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import Swal from "sweetalert2";
import dayjs from "dayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import {
  AMOUNT_OPTIONS,
  getUniqueFieldOptions,
  ITEM_LEDGER_FILTER_FIELDS,
  RETURNABLE_OPTIONS,
} from "./itemLedgerFilterUtils";

const ItemLedgerFilter = ({
  itemLedger = [],
  filteredCount = 0,
  filterField,
  setFilterField,
  filterValue,
  setFilterValue,
  dateFrom,
  setDateFrom,
  dateTo,
  setDateTo,
  onApply,
  onClear,
}) => {
  const typeOptions = getUniqueFieldOptions(itemLedger, "type");
  const entityNumberOptions = getUniqueFieldOptions(itemLedger, "entity_number");
  const customerOptions = getUniqueFieldOptions(itemLedger, "customer_name");
  const locationOptions = getUniqueFieldOptions(itemLedger, "location_name");
  const quantityOptions = getUniqueFieldOptions(itemLedger, "quantity");
  const rateOptions = getUniqueFieldOptions(itemLedger, "rate");

  const getFieldLabel = (fieldId) =>
    ITEM_LEDGER_FILTER_FIELDS.find((field) => field.id === fieldId)?.label ||
    "Value";

  const handleFilterFieldChange = (event) => {
    const nextField = event.target.value;
    setFilterField(nextField);
    setFilterValue("");
    setDateFrom(null);
    setDateTo(null);
  };

  const handleApplyFilter = () => {
    if (!filterField) {
      onApply({});
      return;
    }

    const nextFilters = { filter_field: filterField };

    if (filterField === "date") {
      if (!dateFrom && !dateTo) {
        Swal.fire("Warning", "Please select a date range.", "warning");
        return;
      }
      nextFilters.date_from = dateFrom
        ? dayjs(dateFrom).format("YYYY-MM")
        : null;
      nextFilters.date_to = dateTo ? dayjs(dateTo).format("YYYY-MM") : null;
      nextFilters.filter_value = "";
    } else if (
      filterValue === "" ||
      filterValue === null ||
      filterValue === undefined
    ) {
      Swal.fire("Warning", "Please select or enter a filter value.", "warning");
      return;
    } else {
      nextFilters.filter_value = filterValue;
      nextFilters.date_from = null;
      nextFilters.date_to = null;
    }

    onApply(nextFilters);
  };

  const renderFilterValueInput = () => {
    if (!filterField) {
      return (
        <TextField
          size="small"
          fullWidth
          disabled
          label="Value"
          sx={{ backgroundColor: "white" }}
        />
      );
    }

    const fieldLabel = getFieldLabel(filterField);

    switch (filterField) {
      case "type":
        return (
          <Autocomplete
            size="small"
            options={typeOptions}
            getOptionLabel={(option) => option.label || ""}
            value={typeOptions.find((opt) => opt.value === filterValue) || null}
            onChange={(event, newValue) =>
              setFilterValue(newValue?.value || "")
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label={fieldLabel}
                sx={{ backgroundColor: "white" }}
              />
            )}
          />
        );

      case "entity_number":
        return (
          <Autocomplete
            freeSolo
            size="small"
            options={entityNumberOptions}
            getOptionLabel={(option) =>
              typeof option === "string" ? option : option.label || ""
            }
            inputValue={filterValue}
            onInputChange={(event, newInputValue) =>
              setFilterValue(newInputValue)
            }
            onChange={(event, newValue) =>
              setFilterValue(
                typeof newValue === "string"
                  ? newValue
                  : newValue?.value || ""
              )
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label={fieldLabel}
                sx={{ backgroundColor: "white" }}
              />
            )}
          />
        );

      case "customer_name":
        return (
          <Autocomplete
            size="small"
            options={customerOptions}
            getOptionLabel={(option) => option.label || ""}
            value={
              customerOptions.find((opt) => opt.value === filterValue) || null
            }
            onChange={(event, newValue) =>
              setFilterValue(newValue?.value || "")
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label={fieldLabel}
                sx={{ backgroundColor: "white" }}
              />
            )}
          />
        );

      case "location_name":
        return (
          <Autocomplete
            size="small"
            options={locationOptions}
            getOptionLabel={(option) => option.label || ""}
            value={
              locationOptions.find((opt) => opt.value === filterValue) || null
            }
            onChange={(event, newValue) =>
              setFilterValue(newValue?.value || "")
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label={fieldLabel}
                sx={{ backgroundColor: "white" }}
              />
            )}
          />
        );

      case "quantity":
        return (
          <Autocomplete
            size="small"
            options={quantityOptions}
            getOptionLabel={(option) => option.label || ""}
            value={
              quantityOptions.find((opt) => opt.value === filterValue) || null
            }
            onChange={(event, newValue) =>
              setFilterValue(newValue?.value || "")
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label={fieldLabel}
                sx={{ backgroundColor: "white" }}
              />
            )}
          />
        );

      case "rate":
        return (
          <Autocomplete
            size="small"
            options={rateOptions}
            getOptionLabel={(option) => option.label || ""}
            value={rateOptions.find((opt) => opt.value === filterValue) || null}
            onChange={(event, newValue) =>
              setFilterValue(newValue?.value || "")
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label={fieldLabel}
                sx={{ backgroundColor: "white" }}
              />
            )}
          />
        );

      case "amount":
        return (
          <Autocomplete
            size="small"
            options={AMOUNT_OPTIONS}
            getOptionLabel={(option) => option.label || ""}
            value={
              AMOUNT_OPTIONS.find(
                (opt) => String(opt.value) === String(filterValue)
              ) || null
            }
            onChange={(event, newValue) =>
              setFilterValue(newValue?.value ?? "")
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label={fieldLabel}
                sx={{ backgroundColor: "white" }}
              />
            )}
          />
        );

      case "is_returnable":
        return (
          <Autocomplete
            size="small"
            options={RETURNABLE_OPTIONS}
            getOptionLabel={(option) => option.label || ""}
            value={
              RETURNABLE_OPTIONS.find((opt) => opt.value === filterValue) ||
              null
            }
            onChange={(event, newValue) =>
              setFilterValue(newValue?.value || "")
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label={fieldLabel}
                sx={{ backgroundColor: "white" }}
              />
            )}
          />
        );

      case "date":
        return (
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
              <DatePicker
                label="From Date"
                views={["year", "month"]}
                value={dateFrom}
                onChange={(value) => setDateFrom(value)}
                slotProps={{
                  textField: {
                    size: "small",
                    fullWidth: true,
                    sx: { backgroundColor: "white" },
                  },
                }}
              />
              <DatePicker
                label="To Date"
                views={["year", "month"]}
                value={dateTo}
                onChange={(value) => setDateTo(value)}
                slotProps={{
                  textField: {
                    size: "small",
                    fullWidth: true,
                    sx: { backgroundColor: "white" },
                  },
                }}
              />
            </Stack>
          </LocalizationProvider>
        );

      default:
        return null;
    }
  };

  return (
    <>
      <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <Grid item xs={12} md={3}>
          <FormControl fullWidth size="small" sx={{ backgroundColor: "white" }}>
            <InputLabel>Filter By</InputLabel>
            <Select
              label="Filter By"
              value={filterField}
              onChange={handleFilterFieldChange}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {ITEM_LEDGER_FILTER_FIELDS.map((field) => (
                <MenuItem key={field.id} value={field.id}>
                  {field.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={5}>
          {renderFilterValueInput()}
        </Grid>
        <Grid item xs={12} md={4} sx={{ px: { xs: 0.5, md: 1 } }}>
          <Stack
            direction="row"
            spacing={1}
            sx={{ width: "100%", alignItems: "stretch" }}
          >
            <Button
              variant="contained"
              startIcon={<SearchIcon />}
              onClick={handleApplyFilter}
              sx={{ flex: 1, minWidth: 0, px: 1.5, whiteSpace: "nowrap" }}
            >
              Apply
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<ClearIcon />}
              onClick={onClear}
              sx={{ flex: 1, minWidth: 0, px: 1.5, whiteSpace: "nowrap" }}
            >
              Clear
            </Button>
          </Stack>
        </Grid>
      </Grid>
      <Typography variant="body2" sx={{ mb: 1, color: "text.secondary" }}>
        Showing {filteredCount} of {itemLedger.length} records
      </Typography>
    </>
  );
};

export default memo(ItemLedgerFilter);
